import { useRef, useEffect, useState, type KeyboardEvent } from "react";
import { Dismiss20Regular, Send20Regular, ArrowMaximize20Regular, ArrowMinimize20Regular } from "@fluentui/react-icons";
import type { HelpChatMessage as HelpChatMessageType } from "../../ai/aiApi";
import { HelpChatMessage } from "./HelpChatMessage";

interface HelpChatPanelProps {
  messages: HelpChatMessageType[];
  isLoading: boolean;
  onSend: (text: string) => void;
  onClose: () => void;
}

export function HelpChatPanel({
  messages,
  isLoading,
  onSend,
  onClose,
}: HelpChatPanelProps) {
  const [input, setInput] = useState("");
  const [expanded, setExpanded] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  const handleSend = () => {
    if (!input.trim() || isLoading) return;
    onSend(input);
    setInput("");
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      className="fixed z-50 flex flex-col overflow-hidden"
      style={{
        bottom: "88px",
        right: "24px",
        width: expanded ? "560px" : "380px",
        height: expanded ? "700px" : "520px",
        transition: "width 0.25s ease, height 0.25s ease",
        borderRadius: "24px",
        boxShadow: "0 8px 40px rgba(0,0,0,0.22), 0 2px 8px rgba(0,0,0,0.12)",
        border: "1px solid rgba(0,0,0,0.10)",
        background: "var(--background, #fff)",
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 border-b"
        style={{
          background: "var(--primary)",
          borderColor: "transparent",
        }}
      >
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-white/70" />
          <span className="text-sm font-semibold text-white">
            Assistente AJA2 AI
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setExpanded((v) => !v)}
            className="p-1 rounded-lg text-white/70 hover:text-white hover:bg-white/20 transition-colors"
            aria-label={expanded ? "Reduzir" : "Expandir"}
          >
            {expanded
              ? <ArrowMinimize20Regular className="w-4 h-4" />
              : <ArrowMaximize20Regular className="w-4 h-4" />
            }
          </button>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-white/70 hover:text-white hover:bg-white/20 transition-colors"
            aria-label="Fechar"
          >
            <Dismiss20Regular className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1 bg-accent/30">
        {messages.map((msg, i) => (
          <HelpChatMessage key={i} message={msg} />
        ))}
        {isLoading && (
          <div className="flex justify-start mb-3 items-end gap-2">
            <div
              style={{
                borderRadius: "4px 18px 18px 18px",
                padding: "12px 16px",
                background: "#fff",
                border: "1px solid rgba(0,0,0,0.06)",
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <span style={{ fontSize: "12px", color: "var(--muted-foreground)" }}>A pensar</span>
              <span style={{ display: "flex", gap: "3px", alignItems: "center" }}>
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    style={{
                      display: "inline-block",
                      width: "6px",
                      height: "6px",
                      borderRadius: "50%",
                      background: "var(--primary)",
                      animation: "helpDot 1.2s ease-in-out infinite",
                      animationDelay: `${i * 0.2}s`,
                    }}
                  />
                ))}
              </span>
              <style>{`
                @keyframes helpDot {
                  0%, 60%, 100% { transform: scale(1); opacity: 0.4; }
                  30% { transform: scale(1.4); opacity: 1; }
                }
              `}</style>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-border px-3 py-2.5 bg-background">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Faça uma pergunta sobre a plataforma..."
            className="flex-1 resize-none bg-muted rounded-xl px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none border-0 max-h-24"
            rows={1}
            disabled={isLoading}
            style={{ lineHeight: "1.5" }}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            className="p-2 rounded-xl bg-primary text-primary-foreground disabled:opacity-40 hover:opacity-90 transition-opacity flex-shrink-0"
            aria-label="Enviar"
          >
            <Send20Regular className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-muted-foreground mt-1.5 px-1">
          Enter para enviar · Shift+Enter para nova linha
        </p>
      </div>
    </div>
  );
}
